<script lang="ts">
	import linkedin from "@core/assets/icons/linkedin.svg?raw";
	import fastestSnail from "@core/assets/images/fastest-logo-variants/fastest-horizontal-snail-onyx.svg";
	import type { TeamMember } from "@core/schema/TeamMember.js";
	import { getUsernameFromLinkedinUrl } from "@core/schema/utils/getUsernameFromLinkedinUrl.js";
	import type { Background } from "./Background.js";
	import Icon from "./common/Icon.svelte";
	import Media from "./common/Media.svelte";
	import type { MediaSrc } from "./common/MediaSrc.js";

	type Contact = Pick<TeamMember, "name" | "position" | "phone" | "email" | "linkedin" | "highlighted" | "type"> & {
		image?: MediaSrc;
	};

	const {
		contact,
		background,
		isLight,
		breakNames,
		isBigger,
		imageSize,
	}: {
		contact: Contact;
		background?: Background;
		isLight?: boolean;
		breakNames?: boolean;
		isBigger?: boolean;
		imageSize?: number;
	} = $props();
	const [firstname, lastname] = contact.name.split(" ");
</script>

<div
	class={[
		"flex lg:items-center flex-row gap-4 md:gap-6 lg:gap-9 max-md:w-full max-md:max-w-96 p-4 rounded-xl max-md:!bg-white",
		isBigger ? "md:p-6 lg:gap-7 lg:pr-8" : "",
		background ?? "bg-white",
	]}
>
	{#if contact.image}
		<Media
			width={imageSize ?? 188}
			height={imageSize ?? 188}
			source={contact.image}
			alt={contact.name}
			wrapperClass="shrink-0"
			class="rounded-full aspect-square w-28 md:w-[9.375rem] lg:w-[11.75rem] border-fstonyx border-4 object-cover"
		/>
	{:else}
		<div
			class="aspect-square w-28 h-28 md:h-fit md:w-[9.375rem] lg:w-[11.75rem] border-fstonyx border-4 bg-[#D7D7D7] rounded-full flex items-center justify-center shrink-0"
		>
			<Media source={{ src: fastestSnail, type: "image/svg+xml" }} width={64} height={64} alt="No photo" />
		</div>
	{/if}
	<div class="flex flex-col lg:items-start">
		<h3
			class={[
				"my-0 -tracking-[0.02em] text-md md:text-lg leading-8 max-md:leading-normal font-bold font-loos-wide text-left",
				isLight ? "text-fstonyx" : "text-fstgreen",
			]}
		>
			{#if breakNames}
				<span class="block">{firstname}</span>
				<span class="block">{lastname}</span>
			{:else}
				{contact.name}
			{/if}
		</h3>
		<span
			class={[
				"text-left leading-normal md:text-sm tracking-[0.015em] mb-2 text-fstgreen",
				isBigger ? "md:text-md mt-1" : "md:text-sm",
			]}>{contact.position}</span
		>
		<div class="flex flex-col gap-1 sm:gap-0">
			{#if contact.phone}
				<a
					class={[
						"text-left text-xs max-md:leading-normal tracking-[0.015em]",
						isLight ? "text-fstgrey-600" : "text-white",
						isBigger ? "!text-sm" : "md:text-base",
					]}
					href="tel:{contact.phone}">{contact.phone}</a
				>
			{/if}
			{#if contact.email}
				<a
					class={[
						"text-left text-xs max-md:leading-normal md:text-base tracking-[0.015em] mb-2",
						isLight ? "text-fstgrey-600 text-base" : "text-white font-medium",
						isBigger ? "!text-sm" : "md:text-base",
					]}
					href="mailto:{contact.email}">{contact.email}</a
				>
			{/if}
			{#if contact.linkedin}
				<a
					class={[
						"text-left flex transition-all hover:text-fstgreen hover:underline gap-1 md:gap-1 items-center leading-none text-xs tracking-[0.015em]",
						isLight ? "text-fstgrey-600 !text-base" : "text-white font-medium",
					]}
					target="_blank"
					href={contact.linkedin}
				>
					<Icon icon={linkedin} class="w-3 md:w-4" square />
					<span class="mt-0.5 text-xs lg:text-sm underline">{getUsernameFromLinkedinUrl(contact.linkedin)}</span>
				</a>
			{/if}
		</div>
	</div>
</div>
